import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Eye, Calculator, DollarSign, TrendingUp, TrendingDown, ClipboardList } from 'lucide-react';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { IconButton } from '@/Components/ui/IconButton';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Components/ui/Dialog';
import custoOpService, { type CustoOp } from '@/services/custoOp';
import ordemProducaoService, { type OrdemProducao } from '@/services/ordemProducao';

const STATUS_LABELS: Record<string, string> = {
  ABERTA: 'Aberta',
  LIBERADA: 'Liberada',
  EM_PRODUCAO: 'Em Produção',
  PAUSADA: 'Pausada',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
};
const STATUS_TONE: Record<string, Tone> = {
  ABERTA: 'neutral',
  LIBERADA: 'info',
  EM_PRODUCAO: 'warning',
  PAUSADA: 'purple',
  ENCERRADA: 'success',
  CANCELADA: 'danger',
};

function formatCurrency(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNumber(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatPercent(v: number | null | undefined) {
  return (Number(v) || 0).toFixed(1);
}

function getVarColor(diff: number) {
  if (diff > 0) return 'var(--nx-danger)';
  if (diff < 0) return 'var(--nx-ok)';
  return 'var(--nx-muted)';
}

function getVarLabel(diff: number) {
  return `${diff >= 0 ? '+' : ''}R$ ${formatCurrency(Math.abs(diff))}`;
}

type CustoRow = CustoOp & {
  temCusto: boolean;
};

const STATUS_OP_ANALISAVEIS = ['EM_PRODUCAO', 'PAUSADA', 'ENCERRADA'];

function custoPendenteFromOp(op: OrdemProducao): CustoRow {
  return {
    id: `op-${op.id}`,
    op_id: op.id,
    op_numero: op.numero,
    op_status: op.status,
    produto_codigo: op.produto_codigo || op.produto?.codigo || null,
    produto_nome: op.produto_nome || op.produto_descricao || op.produto?.descricao || null,
    quantidade_produzida: op.quantidade_produzida,
    custo_mp_previsto: 0,
    custo_mp_real: 0,
    custo_mdo_previsto: 0,
    custo_mdo_real: 0,
    custo_indiretos_previsto: 0,
    custo_indiretos_real: 0,
    custo_unitario_padrao: 0,
    custo_unitario_real: 0,
    custo_total_previsto: 0,
    custo_total_real: 0,
    variacao: 0,
    variacao_percentual: 0,
    data_calculo: null,
    temCusto: false,
  };
}

export default function CustosPage() {
  const [items, setItems] = useState<CustoRow[]>([]);
  const [ordens, setOrdens] = useState<OrdemProducao[]>([]);
  const [loading, setLoading] = useState(false);
  const [processando, setProcessando] = useState<string | null>(null);

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroVariacao, setFiltroVariacao] = useState('');

  const [dialogDetalhes, setDialogDetalhes] = useState(false);
  const [itemVisualizar, setItemVisualizar] = useState<CustoRow | null>(null);

  const carregar = async () => {
    setLoading(true);
    try {
      const [custos, ops] = await Promise.all([
        custoOpService.listar(),
        ordemProducaoService.listar(),
      ]);
      setItems(custos.map((custo) => ({ ...custo, temCusto: true })));
      setOrdens(ops);
    } catch (error) {
      console.error('Erro ao carregar custos de OP', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const itensFiltrados = useMemo(() => {
    const opsComCusto = new Set(items.map((item) => item.op_id));
    const pendentes = ordens
      .filter((op) => STATUS_OP_ANALISAVEIS.includes(op.status))
      .filter((op) => !opsComCusto.has(op.id))
      .map(custoPendenteFromOp);
    let resultado = [...items, ...pendentes];

    if (busca) {
      const termo = busca.toLowerCase();
      resultado = resultado.filter(
        (item) => (item.op_numero || '').toLowerCase().includes(termo) || (item.produto_nome || '').toLowerCase().includes(termo)
      );
    }
    if (filtroStatus) {
      resultado = resultado.filter((item) => item.op_status === filtroStatus);
    }
    if (filtroVariacao === 'ACIMA') {
      resultado = resultado.filter((item) => item.temCusto && item.variacao > 0);
    } else if (filtroVariacao === 'ABAIXO') {
      resultado = resultado.filter((item) => item.temCusto && item.variacao < 0);
    } else if (filtroVariacao === 'OK') {
      resultado = resultado.filter((item) => item.temCusto && Math.abs(item.variacao_percentual) <= 5);
    }
    return resultado;
  }, [items, ordens, busca, filtroStatus, filtroVariacao]);

  const totalPrevisto = useMemo(() => items.reduce((sum, item) => sum + (Number(item.custo_total_previsto) || 0), 0), [items]);
  const totalReal = useMemo(() => items.reduce((sum, item) => sum + (Number(item.custo_total_real) || 0), 0), [items]);
  const variacaoTotal = totalReal - totalPrevisto;

  const visualizar = (item: CustoRow) => {
    if (!item.temCusto) return;
    setItemVisualizar(item);
    setDialogDetalhes(true);
  };

  const recalcular = async (item: CustoRow) => {
    setProcessando(item.id);
    try {
      if (item.temCusto) await custoOpService.recalcular(item.id);
      else await custoOpService.calcular(item.op_id);
      await carregar();
    } catch (error) {
      console.error('Erro ao recalcular custo', error);
    } finally {
      setProcessando(null);
    }
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<DollarSign size={21} strokeWidth={2} />}
        title="Custos por Ordem de Produção"
        description="Comparativo entre custos previstos e reais apurados a partir do apontamento de produção."
        actions={
          <Button type="button" variant="ghost" onClick={carregar} loading={loading} loadingText="Atualizando...">
            <RefreshCw size={16} strokeWidth={2.2} />
            Atualizar
          </Button>
        }
      />

      <div className="nx-kpi-grid">
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Total Previsto</span>
            <div className="nx-kpi-icon">
              <Calculator size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">R$ {formatCurrency(totalPrevisto)}</strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Total Real</span>
            <div className="nx-kpi-icon">
              <DollarSign size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">R$ {formatCurrency(totalReal)}</strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">Variação Total</span>
            <div className="nx-kpi-icon">
              {variacaoTotal >= 0 ? <TrendingUp size={16} strokeWidth={2.2} /> : <TrendingDown size={16} strokeWidth={2.2} />}
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value" style={{ color: variacaoTotal >= 0 ? 'var(--nx-danger)' : 'var(--nx-ok)' }}>
              {variacaoTotal >= 0 ? '+' : ''}R$ {formatCurrency(Math.abs(variacaoTotal))}
            </strong>
          </div>
        </article>
        <article className="nx-kpi-card">
          <div className="nx-kpi-header">
            <span className="nx-kpi-title">OPs Analisadas</span>
            <div className="nx-kpi-icon">
              <ClipboardList size={16} strokeWidth={2.2} />
            </div>
          </div>
          <div className="nx-kpi-body">
            <strong className="nx-kpi-value">{itensFiltrados.length}</strong>
          </div>
        </article>
      </div>

      <Card className="nx-page-card">
        <CardHead
          title="Listagem"
          actions={
            <div className="nx-card-head-actions">
              <div className="nx-search-field">
                <Search size={15} strokeWidth={2} aria-hidden />
                <input
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar OP ou produto..."
                />
              </div>
              <Select filter value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
                <option value="">Status OP</option>
                <option value="EM_PRODUCAO">Em Produção</option>
                <option value="PAUSADA">Pausada</option>
                <option value="ENCERRADA">Encerrada</option>
              </Select>
              <Select filter value={filtroVariacao} onChange={(e) => setFiltroVariacao(e.target.value)}>
                <option value="">Variação</option>
                <option value="ACIMA">Acima do previsto</option>
                <option value="ABAIXO">Abaixo do previsto</option>
                <option value="OK">Dentro da margem</option>
              </Select>
            </div>
          }
        />

        {!loading && itensFiltrados.length === 0 ? (
          <EmptyState message="Nenhum custo encontrado." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Nº OP</th>
                  <th>Produto</th>
                  <th>Previsto</th>
                  <th>Real</th>
                  <th>Variação</th>
                  <th>Custo Unit.</th>
                  <th>Status</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="nx-table-loading">Carregando…</td>
                  </tr>
                ) : (
                  itensFiltrados.map((item) => (
                    <tr key={item.id}>
                      <td>{item.op_numero}</td>
                      <td>{item.produto_nome}</td>
                      <td>R$ {formatCurrency(item.custo_total_previsto)}</td>
                      <td>R$ {formatCurrency(item.custo_total_real)}</td>
                      <td>
                        {item.temCusto ? (
                          <StatusBadge
                            label={`${item.variacao >= 0 ? '+' : ''}${formatPercent(item.variacao_percentual)}%`}
                            tone={item.variacao >= 0 ? 'danger' : 'success'}
                          />
                        ) : (
                          <StatusBadge label="Pendente" tone="neutral" />
                        )}
                      </td>
                      <td>R$ {formatCurrency(item.custo_unitario_real)}</td>
                      <td>
                        <StatusBadge
                          label={STATUS_LABELS[item.op_status || ''] || item.op_status || '-'}
                          tone={STATUS_TONE[item.op_status || ''] || 'neutral'}
                        />
                      </td>
                      <td className="nx-col-acoes">
                        <div className="nx-table-actions-group nx-table-actions-group--buttons">
                          <IconButton label="Detalhes" onClick={() => visualizar(item)} disabled={!item.temCusto}>
                            <Eye size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                          <IconButton
                            label={item.temCusto ? 'Recalcular' : 'Calcular'}
                            onClick={() => recalcular(item)}
                            disabled={processando === item.id}
                          >
                            <Calculator size={15} strokeWidth={1.9} aria-hidden />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={dialogDetalhes} onOpenChange={setDialogDetalhes}>
        <DialogContent className="nexus-dialog--wide">
          <DialogHeader>
            <DialogTitle>Custos - OP {itemVisualizar?.op_numero ?? ''}</DialogTitle>
            <DialogDescription>Comparativo entre custos previstos e reais apurados para esta ordem de produção.</DialogDescription>
          </DialogHeader>

          {itemVisualizar && (
            <div>
              <div
                style={{
                  marginBottom: '1rem',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '0.75rem',
                  borderRadius: 'var(--nx-radius-sm)',
                  background: 'var(--nx-accent-soft)',
                  padding: '0.75rem',
                  fontSize: '0.85rem',
                }}
              >
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Produto</span>
                  {itemVisualizar.produto_nome}
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Qtd. Produzida</span>
                  {formatNumber(itemVisualizar.quantidade_produzida)}
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Custo Unitário Padrão</span>
                  R$ {formatCurrency(itemVisualizar.custo_unitario_padrao)}
                </div>
                <div>
                  <span style={{ display: 'block', fontSize: '0.72rem', color: 'var(--nx-muted)' }}>Custo Unitário Real</span>
                  <strong style={{ color: 'var(--nx-accent)' }}>R$ {formatCurrency(itemVisualizar.custo_unitario_real)}</strong>
                </div>
              </div>

              <div className="nx-detail-grid">
                <Card>
                  <CardHead title="Custos Previstos" />
                  <dl className="nx-detail-list">
                    <div>
                      <dt>Matéria-Prima</dt>
                      <dd>R$ {formatCurrency(itemVisualizar.custo_mp_previsto)}</dd>
                    </div>
                    <div>
                      <dt>Mão de Obra</dt>
                      <dd>R$ {formatCurrency(itemVisualizar.custo_mdo_previsto)}</dd>
                    </div>
                    <div>
                      <dt>Custos Indiretos</dt>
                      <dd>R$ {formatCurrency(itemVisualizar.custo_indiretos_previsto)}</dd>
                    </div>
                    <div>
                      <dt>Total Previsto</dt>
                      <dd><strong>R$ {formatCurrency(itemVisualizar.custo_total_previsto)}</strong></dd>
                    </div>
                  </dl>
                </Card>
                <Card>
                  <CardHead title="Custos Reais" />
                  <dl className="nx-detail-list">
                    <div>
                      <dt>Matéria-Prima</dt>
                      <dd>
                        R$ {formatCurrency(itemVisualizar.custo_mp_real)}{' '}
                        <span style={{ color: getVarColor(itemVisualizar.custo_mp_real - itemVisualizar.custo_mp_previsto) }}>
                          ({getVarLabel(itemVisualizar.custo_mp_real - itemVisualizar.custo_mp_previsto)})
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Mão de Obra</dt>
                      <dd>
                        R$ {formatCurrency(itemVisualizar.custo_mdo_real)}{' '}
                        <span style={{ color: getVarColor(itemVisualizar.custo_mdo_real - itemVisualizar.custo_mdo_previsto) }}>
                          ({getVarLabel(itemVisualizar.custo_mdo_real - itemVisualizar.custo_mdo_previsto)})
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Custos Indiretos</dt>
                      <dd>
                        R$ {formatCurrency(itemVisualizar.custo_indiretos_real)}{' '}
                        <span style={{ color: getVarColor(itemVisualizar.custo_indiretos_real - itemVisualizar.custo_indiretos_previsto) }}>
                          ({getVarLabel(itemVisualizar.custo_indiretos_real - itemVisualizar.custo_indiretos_previsto)})
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Total Real</dt>
                      <dd><strong>R$ {formatCurrency(itemVisualizar.custo_total_real)}</strong></dd>
                    </div>
                  </dl>
                </Card>
              </div>
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">Fechar</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
